import { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, InputAdornment, Snackbar, TextField } from "@mui/material";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import AnnouncementBar from "../../../../frontend/pages/shop/components/AnnouncementBar";
import styles from "../shop.module.css";

// Shop Now settings: the minimum order and the banner shown above the shop
const ShopSettings = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { websiteData } = useWebsite();
  useSiteBranding("Shop settings", websiteData);
  const [form, setForm] = useState({ shop_min_order: "", shop_announcement: "" });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  useEffect(() => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shop/settings", {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setForm({ shop_min_order: response.data.data.shop_min_order || "", shop_announcement: response.data.data.shop_announcement || "" });
      }
    }).catch(() => { });
  }, [API_TOKEN]);

  const save = () => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/shop/settings", form, {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        setSnackbar({ open: true, message: "Shop settings saved.", type: "success" });
      } else {
        setSnackbar({ open: true, message: response.data.message, type: "error" });
      }
    }).catch(() => {
      setSaving(false);
      setSnackbar({ open: true, message: "Something went wrong. Please try again.", type: "error" });
    });
  };

  return (<>
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Shop Settings" trail={[{ label: "Shop" }, { label: "Settings" }]} />
      <div className={`${styles.MainCard}`}>
        <div className={`${styles.SettingsForm}`}>
          <div>
            <TextField
              label="Minimum order"
              type="number"
              value={form.shop_min_order}
              onChange={(e) => setForm((f) => ({ ...f, shop_min_order: e.target.value }))}
              inputProps={{ min: 0, step: "0.01" }}
              InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              fullWidth
            />
            <span className={`${styles.SubText}`}>Customers can't check out below this item total. Leave empty or 0 for no minimum.</span>
          </div>
          <div>
            <TextField
              label="Announcement"
              value={form.shop_announcement}
              onChange={(e) => setForm((f) => ({ ...f, shop_announcement: e.target.value }))}
              inputProps={{ maxLength: 200 }}
              placeholder="e.g. Minimum purchase of £30"
              fullWidth
            />
            <span className={`${styles.SubText}`}>A banner across the top of Shop Now. Leave empty to hide it.</span>
          </div>
          {form.shop_announcement.trim() && (<div>
            <span className={`${styles.SubText}`} style={{ marginBottom: 6 }}>Preview</span>
            <div style={{ "--primary-color": websiteData?.home_config?.primaryColor || "#ef233c" }}>
              <AnnouncementBar text={form.shop_announcement} />
            </div>
          </div>)}
          <div>
            <Button className={`${styles.NewOrderBU}`} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save settings"}</Button>
          </div>
        </div>
      </div>
    </div>

    <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
      <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.type} variant="filled" sx={{ width: "100%" }}>{snackbar.message}</Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(ShopSettings);
